import { POApi } from '../POApi'
import { pipe } from 'fp-ts/lib/function'
import * as t from 'io-ts'
import { Maybe } from '@mxt/zio/codec'
import { ZIO, Task } from '@mxt/zio'
import { CancelRider, ProductEnum } from './model'
import { SubmissionService } from './SubmissionService'
import { TopUpService } from './TopUpService'
import { CancelRiderService } from './CancelRiderService'
import * as O from 'fp-ts/lib/Option'

export namespace RiderAlterationService {
  const ErrorType = Maybe(
    t.type({
      code: Maybe(t.number),
      message: Maybe(t.string)
    })
  )
  const ClassItem = t.type({
    riderCode: t.string,
    classList: t.array(t.string)
  })

  const RiderNameInfo = t.type({
    id: Maybe(t.string),
    code: t.string,
    name: t.string,
    nameVi: t.string,
    enable: Maybe(t.boolean)
  })

  const T94RistType = t.type({
    riskType: t.string,
    productCode: t.string,
    enable: Maybe(t.boolean)
  })

  // const healthCareBenefitInfo = t.type()

  const HealthcareMasterDataResponse = t.type({
    body: t.type({
      healthCareBenefitList: t.array(t.type({
        name: t.string,
        valueVi: t.string,
        valueEn: t.string,
      })),
      ipOptionList: t.array(t.type({
        name: t.string,
        value: t.string
      })),
      opOptionList: t.array(t.type({
        name: t.string,
        value: t.string
      })),
      deOptionList: t.array(t.type({
        name: t.string,
        value: t.string
      })),
      maOptionList: t.array(t.type({
        name: t.string,
        value: t.string
      }))
    })
  })

  const WithdrawalHCRInfo = t.type({
    bankName: t.string,
    bankCode: t.string,
    effectiveTo: t.string,
    riderCodeList: t.array(t.string)
  })

  export type ClassItem = t.TypeOf<typeof ClassItem>
  export type RiderNameInfo = t.TypeOf<typeof RiderNameInfo>
  export type T94RistType = t.TypeOf<typeof T94RistType>
  export type HealthcareMasterDataResponse = t.TypeOf<typeof HealthcareMasterDataResponse>
  export type WithdrawalHCRInfo = t.TypeOf<typeof WithdrawalHCRInfo>

  export const getDetail = (policyNum: string, policyData: SubmissionService.PolicyType): Task<CancelRider.DetailCancelRiderInfo> =>
    pipe(
      ZIO.zipPar(
        CancelRiderService.getClientRiderInfo(policyNum),
        TopUpService.GetPolicyExtraInfo([policyNum], ''),
        // SubmissionService.getPolicy(policyNum),
        CancelRiderService.getRiderInfoList(),
        SubmissionService.getAgentInfo(policyNum),
      ),
      ZIO.flatMap(([clients, policyExtra, riderNameList, agentInfo]) => {
        const servicingAgentCode = pipe(
          O.fromNullable(policyData.servingAgents?.agent),
          O.fold(
            () => '',
            (servingAgents) => servingAgents.code || ''
          )
        )
        return pipe(servicingAgentCode.startsWith('69')
          ? getWithdrawalHCRList()
          : ZIO.succeed([]),
          ZIO.map((withdrawalHCRList) => ({ clients, policyExtra, riderNameList, agentInfo, withdrawalHCRList }))
        )
      }),
      ZIO.map(({ clients, policyExtra, riderNameList, agentInfo, withdrawalHCRList }) => {
        const sumAssuredOfBasic = pipe(
          O.fromNullable(policyExtra.policyExtraInfoDetail.find((x) => x.lifeNo === '01' && x.coverageNo === '01' && x.riderNo === '00'),),
          O.map((x) => Number(x.sumAssured)),
          O.getOrElse(() => 0)
        )
        const premCessDateOfPolicy = pipe(
          O.fromNullable(policyExtra.policyExtraInfoDetail.find((x) => x.lifeNo === '01' && x.coverageNo === '01' && x.riderNo === '00'),),
          O.map((x) => x.premCessDate),
          O.getOrElse(() => '')
        )
        const coverageCode = pipe(
          O.fromNullable(policyData.basicCode),
          O.fold(
            () => '',
            (basicCode) => basicCode
          )
        )
        const riskCessDateOfPolicy = pipe(
          O.fromNullable(policyExtra.policyExtraInfoDetail.find((x) => x.lifeNo === '01' && x.coverageNo === '01' && x.riderNo === '00'),),
          O.map((x) => x.riskCessDate),
          O.getOrElse(() => '')
        )
        const servicingAgentCode = pipe(
          O.fromNullable(policyData.servingAgents?.agent),
          O.fold(
            () => '',
            (servingAgents) => servingAgents.code || ''
          )
        )
        const agentBankCode = pipe(
          O.fromNullable(agentInfo.attributes),
          O.fold(
            () => '',
            (agentInfo) => agentInfo.gACode
          )
        )
        const LARiderList = clients.map((clientItem) => {
          // filter x.lifeNo !=="01", x.coverageNo !=="01", x.riderNo !=="00" to get only rider products
          const cancelRiderList = policyExtra.policyExtraInfoDetail
            .filter(
              (x) =>
                (x.lifeNo !== '01' || x.coverageNo !== '01' || x.riderNo !== '00') &&
                x.lifeNo === clientItem.life &&
                x.riderStatus === ProductEnum.Status.IF &&
                !CancelRider.UnCancelRiderList.includes(x.productCode)
            )
            .map((extraItem) => ({
              productCode: String(extraItem?.productCode),
              productName: '',
              riskCessDate: String(extraItem?.riskCessDate),
              riskCommDate: String(extraItem?.riskCommDate),
              lapsedDate: '',
              sumAssured: Number(extraItem?.sumAssured),
              installmentPrem: Number(extraItem?.installmentPremium),
              newInstallmentPremium: 0,
              lifeNo: extraItem.lifeNo,
              coverageNo: extraItem.coverageNo,
              riderNo: extraItem.riderNo,
              riderStatus: extraItem.riderStatus
            }))
          const filterRiderlist = policyExtra.policyExtraInfoDetail
            .filter((item) => item.lifeNo === '01' && item.coverageNo === '01' && item.riderNo === '00')
            .map((extra) => ({
              productCode: String(extra?.productCode),
              sumAssured: Number(extra?.sumAssured || 0)
            }))
          return {
            clientName: clientItem.clientName,
            lifeAssured: clientItem.clientNumberOfLA,
            lifeNo: clientItem.life,
            riders: cancelRiderList,
            extraRider: filterRiderlist,
            LADateOfBirth: clientItem.dateOfBirth || ''
          }
        })
        return {
          totalPremium: Number(policyData.totalPremium || 0),
          laRiderInfo: LARiderList,
          minPremium: 0,
          riderNameList: riderNameList,
          riderNameListTwo: LARiderList.filter((x) => x.extraRider && x.extraRider.length > 0),
          sumAssuredOfBasic: sumAssuredOfBasic,
          coverageCode: coverageCode,
          premCessDateOfPolicy: premCessDateOfPolicy,
          riskCessDateOfPolicy: riskCessDateOfPolicy,
          servicingAgentCode: servicingAgentCode,
          agentBankCode: agentBankCode,
          withdrawalHCRList: withdrawalHCRList
        }
      })
    )

  export const getWaiverProducts = (tableName: string, riderCode: string) =>
    pipe(
      POApi.get(`wf-api/rider/get-items-by?tableName=${tableName}&riderCode=${riderCode}`)(
        t.type({
          body: t.array(t.string),
          responseStatus: ErrorType
        })
      ),
      ZIO.map((response) => {
        return response.body
      })
    )
  export const getRiderList = (policyNum: string, lifeNo: string) =>
    pipe(
      POApi.get(`wf-api/rider/get-list/${policyNum}/${lifeNo}`)(
        t.type({
          body: t.array(RiderNameInfo),
          responseStatus: ErrorType
        })
      ),
      ZIO.map((response) => {
        return {
          lifeNo,
          riderArr: response.body
        }
      })
    )

  export const getClassList = (tableName: string) =>
    pipe(
      POApi.get(`wf-api/rider/get-items/${tableName}`)(
        t.type({
          body: t.array(ClassItem)
        })
      ),
      ZIO.map((response) => response.body)
    )

  export const getRiderNameInfoList = (): Task<RiderNameInfo[]> =>
    pipe(
      POApi.get(`wf-api/rider/get-list`)(
        t.type({
          body: t.array(RiderNameInfo),
          responseStatus: Maybe(
            t.type({
              code: t.number,
              message: t.string
            })
          )
        })
      ),
      ZIO.map((riderInfoList) => {
        return riderInfoList.body
      })
    )

  export const getT94RistTypeList = () => pipe(
    POApi.get(`wf-api/table/t94-risk-type/get-all`)(t.type({
      body: t.array(T94RistType)
    })),
    ZIO.map((riskTypeList) => {
      return riskTypeList.body
    })
  )

  export const getHealthcareBenefitMasterData = (productType: string) => pipe(
    POApi.get(`wf-api/general/data/rider/get-data-common/${productType}`)(HealthcareMasterDataResponse),
    ZIO.map((response) => response)
  )

  export const getWithdrawalHCRList = () => pipe(
    POApi.get(`wf-api/rider/bank-withdraw`)(t.type({
      body: t.array(WithdrawalHCRInfo)
    })),
    ZIO.map((response) => {
      return response.body
    })
  )
}
